import { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ColumnDef, RowAction, SortConfig } from '@/types/list.types';
import { SortableHeader } from './SortableHeader';
import { SkeletonTable } from './SkeletonTable';
import { EmptyState } from '@/Components/shared/EmptyState';
import { Can } from '@/Components/auth/Can';

interface ListTableProps<T> {
  /** Données à afficher */
  data: T[];
  /** Définition des colonnes */
  columns: ColumnDef<T>[];
  /** État de chargement */
  isLoading?: boolean;
  /** Erreur éventuelle */
  error?: Error | null;
  /** Configuration du tri */
  sortConfig?: SortConfig;
  /** Handler de changement de tri */
  onSortChange?: (sortBy: string, sortDirection: 'ASC' | 'DESC') => void;
  /** Actions disponibles pour chaque ligne */
  rowActions?: RowAction<T>[];
  /** Classes CSS conditionnelles pour les lignes */
  rowClassName?: (row: T) => string;
  /** Handler de click sur une ligne */
  onRowClick?: (row: T) => void;
  /** Message si liste vide */
  emptyMessage?: string;
  /** Fonction pour obtenir la clé unique d'une ligne */
  getRowKey?: (row: T, index: number) => string | number;
  /** Activer le réordonnancement */
  enableReorder?: boolean;
  /** Handler appelé lors du réordonnancement */
  onReorder?: (oldIndex: number, newIndex: number) => void;
  /** Permission requise pour réordonner */
  reorderPermission?: string;
  /** Forcer les titres de colonnes sur une seule ligne */
  singleLineHeaders?: boolean;
}

// --- SortableRow sub-component ---
interface SortableRowProps<T> {
  id: string | number;
  row: T;
  index: number;
  columns: ColumnDef<T>[];
  visibleActions: RowAction<T>[];
  isClickable: boolean;
  customRowClassName: string;
  onRowClick?: (row: T) => void;
  enableReorder: boolean;
  reorderPermission?: string;
}

function SortableRow<T>({
  id,
  row,
  columns,
  visibleActions,
  isClickable,
  customRowClassName,
  onRowClick,
  enableReorder,
  reorderPermission,
}: SortableRowProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled: !enableReorder });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const dragProps = enableReorder ? { ...listeners, ...attributes } : {};

  const rowContent = (
    <tr
      ref={setNodeRef}
      style={style}
      onClick={() => onRowClick?.(row)}
      {...dragProps}
      className={`${
        isClickable
          ? 'hover:tw-bg-gray-50 tw-cursor-pointer'
          : 'hover:tw-bg-gray-50'
      } tw-transition-colors tw-select-none ${enableReorder ? 'tw-cursor-grab active:tw-cursor-grabbing' : ''} ${customRowClassName} ${isDragging ? 'tw-z-50' : ''}`}
    >
      {columns.map((column) => (
        <td
          key={column.key}
          className={`tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 ${column.className || ''}`}
        >
          {column.render(row)}
        </td>
      ))}

      {visibleActions.length > 0 && (
        <td className="tw-px-4 tw-py-3 tw-text-sm tw-text-center tw-whitespace-nowrap">
          <div className="tw-flex tw-gap-2 tw-justify-center">
            {visibleActions.map((action) => {
              const isDisabled = action.disabled?.(row) ?? false;
              const Icon = action.icon;
              const variantClasses = {
                primary: 'tw-text-affinicar-primary hover:tw-text-affinicar-dark',
                secondary: 'tw-text-gray-600 hover:tw-text-gray-900',
                danger: 'tw-text-red-600 hover:tw-text-red-900',
                ghost: 'tw-text-gray-400 hover:tw-text-gray-600',
              };
              const buttonClass = variantClasses[action.variant || 'secondary'];

              const buttonElement = action.render ? (
                <div key={action.label}>{action.render(row)}</div>
              ) : (
                <button
                  key={action.label}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isDisabled) {
                      action.onClick(row);
                    }
                  }}
                  disabled={isDisabled}
                  className={`${buttonClass} disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors ${action.className || ''}`}
                  title={action.label}
                >
                  {Icon && <Icon className="tw-w-4 tw-h-4" />}
                </button>
              );

              if (action.permission) {
                return (
                  <Can key={action.label} permission={action.permission}>
                    {buttonElement}
                  </Can>
                );
              }

              return buttonElement;
            })}
          </div>
        </td>
      )}
    </tr>
  );

  // If reorder requires permission, wrap with Can
  if (enableReorder && reorderPermission) {
    return rowContent;
  }

  return rowContent;
}

export function ListTable<T>({
  data,
  columns,
  isLoading = false,
  error = null,
  sortConfig,
  onSortChange,
  rowActions = [],
  rowClassName,
  onRowClick,
  emptyMessage = 'Aucun élément à afficher',
  getRowKey = (_, index) => index,
  enableReorder = false,
  onReorder,
  reorderPermission,
  singleLineHeaders = false,
}: ListTableProps<T>) {
  const [activeId, setActiveId] = useState<string | number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  // Filtrer les actions visibles pour une ligne
  const getVisibleActions = (row: T) => {
    return rowActions.filter((action) => action.visible?.(row) ?? true);
  };

  const hasActions = rowActions.length > 0;

  // État de chargement
  if (isLoading) {
    return <SkeletonTable columns={columns.length + (hasActions ? 1 : 0)} />;
  }

  // État d'erreur
  if (error) {
    return (
      <EmptyState
        icon={<AlertCircle className="tw-w-12 tw-h-12" />}
        title="Erreur de chargement"
        description={error.message}
      />
    );
  }

  // Liste vide
  if (data.length === 0) {
    return <EmptyState title={emptyMessage} />;
  }

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((_, i) => getRowKey(data[i], i).toString() === active.id.toString());
    const newIndex = data.findIndex((_, i) => getRowKey(data[i], i).toString() === over.id.toString());
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder?.(oldIndex, newIndex);
  };

  const rowIds = data.map((row, index) => getRowKey(row, index).toString());

  const activeIndex = activeId !== null
    ? data.findIndex((_, i) => getRowKey(data[i], i).toString() === activeId.toString())
    : -1;
  const activeRow = activeIndex >= 0 ? data[activeIndex] : null;

  const tableContent = (
    <div className="tw-overflow-x-auto tw-bg-white tw-rounded-lg tw-shadow-md">
      <table className="tw-min-w-full tw-divide-y tw-divide-gray-200">
        <thead className="tw-bg-gradient-to-br tw-from-[#8074af] tw-to-[#575fa7]">
          <tr>
            {columns.map((column) => {
              const isSortable = column.sortable !== false && onSortChange;
              const sortKey = column.sortKey || column.key;

              if (isSortable) {
                return (
                  <SortableHeader
                    key={column.key}
                    label={column.label}
                    sortKey={sortKey}
                    currentSort={sortConfig}
                    onSort={onSortChange}
                    className={column.headerClassName}
                    nowrap={singleLineHeaders}
                  />
                );
              }

              return (
                <th
                  key={column.key}
                  className={`tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider ${singleLineHeaders ? 'tw-whitespace-nowrap' : ''} ${column.headerClassName || ''}`}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {column.label}
                </th>
              );
            })}
            {hasActions && (
              <th className="tw-px-4 tw-py-3 tw-text-center tw-text-xs tw-font-medium tw-text-white tw-uppercase tw-tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
          {data.map((row, index) => {
            const key = getRowKey(row, index);
            const customRowClassName = rowClassName?.(row) || '';
            const isClickable = !!onRowClick;
            const visibleActions = getVisibleActions(row);

            return (
              <SortableRow
                key={key}
                id={key.toString()}
                row={row}
                index={index}
                columns={columns}
                visibleActions={visibleActions}
                isClickable={isClickable}
                customRowClassName={customRowClassName}
                onRowClick={onRowClick}
                enableReorder={enableReorder}
                reorderPermission={reorderPermission}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );

  if (!enableReorder) return tableContent;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
        {tableContent}
      </SortableContext>
      <DragOverlay>
        {activeRow ? (
          <table className="tw-min-w-full tw-bg-white tw-shadow-2xl tw-rounded-lg tw-opacity-90">
            <tbody>
              <tr className="tw-bg-gray-50">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`tw-px-4 tw-py-3 tw-text-sm tw-text-gray-900 ${column.className || ''}`}
                  >
                    {column.render(activeRow)}
                  </td>
                ))}
                {hasActions && <td className="tw-px-4 tw-py-3" />}
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
